import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Activity, ClipboardCheck, Layers3, Send, Users, Workflow } from "lucide-react";
import {
  DASHBOARD_PREFS_EVENT,
  resolveDashboardColors,
} from "../dashboardAppearancePreferences";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIEW_CONFIG = {
  sales_ops: {
    title: "My Sales Ops Dashboard",
    subtitle: "Your assigned design requests and artwork projects.",
    tiles: [
      ["all_active", "My Active", "Artwork projects assigned to you", Workflow, "/kanban"],
      ["in_review", "My In Review", "Requests and reviews requiring attention", ClipboardCheck, "/intake-submissions"],
      ["pending_graphics", "Pending Graphics", "Your approved requests waiting for Graphics", Send, "/intake-submissions?status=submitted_to_graphics"],
      ["ready_for_submission", "Graphics Prepress", "Your projects currently in Graphics prepress", Layers3, "/kanban?phase=ready_for_submission"],
    ],
  },
  sales_management: {
    title: "Sales Ops Management Dashboard",
    subtitle: "The complete Sales Ops team workload and approval queue.",
    tiles: [
      ["all_active", "Team Active", "Active projects across the Sales Ops team", Workflow, "/kanban"],
      ["in_review", "Team In Review", "Sales Ops and Graphics reviews requiring attention", ClipboardCheck, "/intake-submissions"],
      ["pending_graphics", "Pending Graphics", "Approved team requests waiting for Graphics", Send, "/intake-submissions?status=submitted_to_graphics"],
      ["ready_for_submission", "Graphics Prepress", "Team projects currently in Graphics prepress", Layers3, "/kanban?phase=ready_for_submission"],
    ],
  },
  graphics_management: {
    title: "Graphics Management Dashboard",
    subtitle: "Full visibility across Sales Ops intake and the Graphics workflow.",
    tiles: [
      ["all_active", "All Active", "All projects currently moving through Graphics", Workflow, "/kanban"],
      ["in_review", "In Review", "Sales Ops or Graphics review requires attention", ClipboardCheck, "/intake-submissions"],
      ["pending_graphics", "Pending Graphics", "Sales Ops-approved requests waiting for Graphics", Send, "/intake-submissions?status=submitted_to_graphics"],
      ["ready_for_submission", "Graphics Prepress", "Projects currently in Graphics prepress", Layers3, "/kanban?phase=ready_for_submission"],
    ],
  },
};

const formatTime = (value) => {
  try { return new Date(value).toLocaleString(); } catch { return value || ""; }
};

const accentStyle = (color) => ({
  borderColor: `${color}99`,
  borderTopColor: color,
  boxShadow: `inset 0 3px 0 ${color}`,
});

export const DashboardPageV8 = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appearance, setAppearance] = useState(resolveDashboardColors);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/workflow/dashboard`)
      .then((response) => setData(response.data))
      .catch((error) => toast.error(error.response?.data?.detail || "Failed to load workflow dashboard"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const refreshAppearance = () => setAppearance(resolveDashboardColors());
    window.addEventListener("storage", refreshAppearance);
    window.addEventListener("focus", refreshAppearance);
    window.addEventListener(DASHBOARD_PREFS_EVENT, refreshAppearance);
    return () => {
      window.removeEventListener("storage", refreshAppearance);
      window.removeEventListener("focus", refreshAppearance);
      window.removeEventListener(DASHBOARD_PREFS_EVENT, refreshAppearance);
    };
  }, []);

  if (loading) return <div className="animate-pulse text-muted-foreground">Loading workflow dashboard...</div>;

  const viewName = data?.dashboard_view || "sales_ops";
  const config = VIEW_CONFIG[viewName] || VIEW_CONFIG.sales_ops;
  const tiles = data?.tiles || {};
  const byPhase = data?.by_phase || {};
  const phaseLabels = data?.phase_labels || {};
  const recentChanges = data?.recent_status_changes || [];
  const team = data?.sales_ops_team || [];
  const tileColors = appearance.tileColors || {};
  const phaseColors = appearance.phaseColors || {};

  return (
    <div className="animate-fade-in flex flex-col gap-4 xl:h-[calc(100vh-4rem)] xl:overflow-hidden">
      <div className="shrink-0">
        <h1 className="text-3xl font-bold">{config.title}</h1>
        <p className="text-muted-foreground mt-1">{config.subtitle}</p>
      </div>

      <div className="grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {config.tiles.map(([key, label, description, Icon, target]) => {
          const color = tileColors[key] || "#334155";
          return (
            <button
              key={key}
              type="button"
              onClick={() => navigate(target)}
              className="stat-card cursor-pointer p-4 text-left transition-all hover:-translate-y-0.5 hover:brightness-110"
              style={accentStyle(color)}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-3xl font-bold leading-none" style={{ color }}>{tiles[key] || 0}</div>
                  <div className="stat-label mt-1">{label}</div>
                </div>
                <span className="rounded-lg p-2" style={{ backgroundColor: `${color}33`, color }}><Icon size={19} /></span>
              </div>
              <p className="mt-2 text-xs leading-4 text-muted-foreground">{description}</p>
            </button>
          );
        })}
      </div>

      {team.length > 0 && (
        <section className="shrink-0 overflow-hidden rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <h2 className="text-base font-semibold">Sales Ops Team Workload</h2>
              <p className="text-xs text-muted-foreground">Current work by assigned Sales Ops team member.</p>
            </div>
            <Users size={19} className="text-muted-foreground" />
          </div>
          <div className="max-h-[230px] overflow-auto [&_th]:!py-2 [&_td]:!py-2">
            <table className="data-table text-sm">
              <thead>
                <tr>
                  <th>Sales Ops</th>
                  <th>Active Projects</th>
                  <th>Awaiting Review</th>
                  <th>Pending Graphics</th>
                  <th>Graphics Prepress</th>
                  <th>Graphics Complete</th>
                </tr>
              </thead>
              <tbody>
                {team.map((row) => (
                  <tr key={row.sales_ops}>
                    <td className="font-medium">{row.sales_ops}</td>
                    <td style={{ color: phaseColors.artwork_submitted }}>{row.active_projects}</td>
                    <td style={{ color: phaseColors.pdf_proof }}>{row.awaiting_review}</td>
                    <td style={{ color: phaseColors.awaiting_graphics_approval }}>{row.pending_graphics}</td>
                    <td style={{ color: phaseColors.ready_for_submission }}>{row.ready_to_send}</td>
                    <td style={{ color: phaseColors.graphics_complete }}>{row.graphics_complete}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 xl:grid-cols-2">
        <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card p-4">
          <div className="mb-3 flex shrink-0 items-center justify-between">
            <div>
              <h2 className="text-base font-semibold">Workflow Phases</h2>
              <p className="text-xs text-muted-foreground">Open a phase in Kanban.</p>
            </div>
            <Workflow size={19} className="text-muted-foreground" />
          </div>
          <div className="grid min-h-0 grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
            {Object.entries(phaseLabels).map(([phase, label]) => {
              const color = phaseColors[phase] || "#334155";
              return (
                <Link
                  key={phase}
                  to={`/kanban?phase=${phase}`}
                  className="flex items-center justify-between gap-3 rounded-lg border bg-background px-3 py-2.5 transition-all hover:-translate-y-0.5 hover:brightness-110"
                  style={{ borderColor: `${color}88`, boxShadow: `inset 3px 0 0 ${color}` }}
                >
                  <span className="text-sm font-medium">{label}</span>
                  <span className="text-lg font-bold" style={{ color }}>{byPhase[phase] || 0}</span>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card p-4">
          <div className="mb-3 flex shrink-0 items-center justify-between">
            <div>
              <h2 className="text-base font-semibold">Recent Status Changes</h2>
              <p className="text-xs text-muted-foreground">Only project status changes appear here.</p>
            </div>
            <Activity size={19} className="text-muted-foreground" />
          </div>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1 overscroll-contain">
            {recentChanges.map((item) => (
              <Link
                key={item.id}
                to={`/projects/${item.project_id}`}
                className="block rounded-lg border border-border bg-background px-3 py-2.5 hover:border-primary/50"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{item.project_name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{item.description}</p>
                  </div>
                  <span className="whitespace-nowrap text-[11px] text-muted-foreground">{formatTime(item.created_at)}</span>
                </div>
              </Link>
            ))}
            {recentChanges.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">No recent status changes</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};
